import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { TASK_TYPE_LABELS } from "@/lib/dictionaries";

const SOURCE_COLOR = {
    airbnb: "#FF5A5F",
    booking: "#003580",
    vrbo: "#3D6CB9",
    avaibook: "#0EA5E9",
    avantio: "#7C3AED",
    manual: "#6F8F72",
    other: "#94A3B8",
};

const SOURCE_LABEL = {
    airbnb: "Airbnb",
    booking: "Booking.com",
    vrbo: "Vrbo",
    avaibook: "Avaibook",
    avantio: "Avantio",
    manual: "Manual",
    other: "Otro",
};

function startOfWeek(d) {
    const x = new Date(d);
    const day = x.getDay() || 7; // make Sunday=7
    x.setDate(x.getDate() - day + 1);
    x.setHours(0, 0, 0, 0);
    return x;
}

function fmtDate(d) {
    return d.toISOString().slice(0, 10);
}

export default function PortalCalendar() {
    const [props, setProps] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
    const [range, setRange] = useState(14); // days

    const from = useMemo(() => fmtDate(anchor), [anchor]);
    const to = useMemo(() => {
        const d = new Date(anchor);
        d.setDate(d.getDate() + range - 1);
        return fmtDate(d);
    }, [anchor, range]);

    useEffect(() => {
        api.get("/portal/properties").then((r) => setProps(r.data || []));
    }, []);

    useEffect(() => {
        setLoading(true);
        api.get(`/portal/calendar?from_=${from}&to=${to}`)
            .then((r) => {
                setBookings(r.data?.bookings || []);
                setTasks(r.data?.tasks || []);
            })
            .finally(() => setLoading(false));
    }, [from, to]);

    const days = useMemo(() => {
        const arr = [];
        const d = new Date(anchor);
        for (let i = 0; i < range; i++) {
            const x = new Date(d);
            x.setDate(d.getDate() + i);
            arr.push(x);
        }
        return arr;
    }, [anchor, range]);

    const goPrev = () => {
        const d = new Date(anchor);
        d.setDate(d.getDate() - range);
        setAnchor(d);
    };
    const goNext = () => {
        const d = new Date(anchor);
        d.setDate(d.getDate() + range);
        setAnchor(d);
    };
    const goToday = () => setAnchor(startOfWeek(new Date()));

    const tasksByProp = useMemo(() => {
        const m = {};
        tasks.forEach((t) => {
            if (!m[t.property_id]) m[t.property_id] = [];
            m[t.property_id].push(t);
        });
        return m;
    }, [tasks]);
    const bookingsByProp = useMemo(() => {
        const m = {};
        bookings.forEach((b) => {
            if (!m[b.property_id]) m[b.property_id] = [];
            m[b.property_id].push(b);
        });
        return m;
    }, [bookings]);

    const todayStr = fmtDate(new Date());
    const colCount = days.length;
    const cellWidth = 100 / colCount;

    return (
        <div className="space-y-5" data-testid="portal-calendar">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Planificación
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Calendario
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Reservas y servicios próximos por propiedad.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={goPrev} className="zentra-btn-ghost" data-testid="portal-cal-prev">
                        <ChevronLeft size={16} />
                    </button>
                    <button onClick={goToday} className="zentra-btn-ghost" data-testid="portal-cal-today">
                        <RotateCcw size={14} /> Hoy
                    </button>
                    <button onClick={goNext} className="zentra-btn-ghost" data-testid="portal-cal-next">
                        <ChevronRight size={16} />
                    </button>
                    <div className="flex items-center gap-1 ml-2">
                        {[7, 14, 30].map((r) => (
                            <button
                                key={r}
                                onClick={() => setRange(r)}
                                className={`px-2.5 py-1.5 text-xs font-semibold rounded-md border ${
                                    range === r ? "bg-zentra-primary text-white border-zentra-primary" : "bg-white text-zentra-ink border-zentra-border"
                                }`}
                                data-testid={`portal-cal-range-${r}`}
                            >
                                {r === 7 ? "1 sem" : r === 14 ? "2 sem" : "1 mes"}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="zentra-card-static overflow-hidden">
                {loading ? (
                    <div className="zentra-skeleton h-72" />
                ) : props.length === 0 ? (
                    <p className="p-10 text-center text-sm text-zentra-muted">Aún no tienes propiedades asignadas al portal.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <div className="min-w-[900px]">
                            {/* Date header */}
                            <div className="flex border-b border-zentra-border bg-[#FAFAF6]">
                                <div className="w-56 shrink-0 px-3 py-2 text-[11px] font-semibold tracking-widest uppercase text-zentra-muted border-r border-zentra-border">
                                    Propiedad
                                </div>
                                <div className="flex-1 flex">
                                    {days.map((d, i) => {
                                        const ds = fmtDate(d);
                                        const isToday = ds === todayStr;
                                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                        return (
                                            <div
                                                key={i}
                                                className={`text-center py-2 text-[10px] uppercase tracking-wider font-semibold border-r border-zentra-border last:border-r-0 ${
                                                    isToday ? "bg-zentra-accent/15 text-zentra-primary" : isWeekend ? "bg-zentra-bg text-zentra-muted" : "text-zentra-muted"
                                                }`}
                                                style={{ width: `${cellWidth}%` }}
                                            >
                                                {d.toLocaleDateString("es-ES", { weekday: "short" }).slice(0, 2)}
                                                <div className="text-zentra-ink font-display text-xs mt-0.5">{d.getDate()}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Rows per property */}
                            {props.map((p) => (
                                <div key={p.id} className="flex border-b border-zentra-border last:border-b-0 hover:bg-[#FAFAF6]" data-testid={`portal-cal-row-${p.code}`}>
                                    <div className="w-56 shrink-0 px-3 py-3 border-r border-zentra-border">
                                        <p className="text-[10px] font-mono-zentra text-zentra-muted">{p.code}</p>
                                        <p className="text-sm font-medium text-zentra-ink truncate">{p.name}</p>
                                    </div>
                                    <div className="flex-1 relative h-16">
                                        {/* day separator lines */}
                                        {days.map((d, i) => {
                                            const ds = fmtDate(d);
                                            const isToday = ds === todayStr;
                                            return (
                                                <div
                                                    key={i}
                                                    className={`absolute top-0 bottom-0 border-r border-zentra-border ${
                                                        isToday ? "bg-zentra-accent/10" : ""
                                                    }`}
                                                    style={{ left: `${cellWidth * i}%`, width: `${cellWidth}%` }}
                                                />
                                            );
                                        })}
                                        {/* bookings */}
                                        {(bookingsByProp[p.id] || []).map((b) => {
                                            const ci = new Date(b.checkin);
                                            const co = new Date(b.checkout);
                                            const start = Math.max(0, Math.floor((ci - anchor) / (1000 * 60 * 60 * 24)));
                                            const end = Math.min(range, Math.floor((co - anchor) / (1000 * 60 * 60 * 24)));
                                            if (end <= 0 || start >= range) return null;
                                            const left = (start / range) * 100;
                                            const width = ((end - start) / range) * 100;
                                            const color = SOURCE_COLOR[b.source] || SOURCE_COLOR.other;
                                            return (
                                                <div
                                                    key={b.id}
                                                    className="absolute top-2.5 h-6 rounded-md text-[10px] font-semibold text-white flex items-center px-2 truncate"
                                                    style={{
                                                        left: `${left}%`,
                                                        width: `calc(${width}% - 4px)`,
                                                        background: color,
                                                    }}
                                                    title={`${b.guest_name || "Huésped"} · ${SOURCE_LABEL[b.source] || b.source}`}
                                                >
                                                    {b.guest_name || "Huésped"}
                                                </div>
                                            );
                                        })}
                                        {/* tasks dots */}
                                        {(tasksByProp[p.id] || []).map((t) => {
                                            const d = new Date(t.scheduled_date);
                                            const idx = Math.floor((d - anchor) / (1000 * 60 * 60 * 24));
                                            if (idx < 0 || idx >= range) return null;
                                            const left = (idx / range) * 100 + cellWidth / 2;
                                            return (
                                                <div
                                                    key={t.id}
                                                    className="absolute bottom-2 -translate-x-1/2 z-10"
                                                    style={{ left: `${left}%` }}
                                                    title={`${TASK_TYPE_LABELS[t.type] || t.type} · ${t.scheduled_date}`}
                                                >
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-zentra-accent/90 text-zentra-primary border border-zentra-accent">
                                                        <span className="w-1 h-1 rounded-full bg-zentra-primary" />
                                                        {(TASK_TYPE_LABELS[t.type] || t.type).slice(0, 7)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-zentra-muted">
                <span className="font-semibold uppercase tracking-wider text-zentra-ink mr-1">Canales:</span>
                {Object.entries(SOURCE_LABEL).map(([k, label]) => (
                    <span key={k} className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: SOURCE_COLOR[k] }} />
                        {label}
                    </span>
                ))}
                <span className="inline-flex items-center gap-1.5 ml-3">
                    <span className="w-2 h-2 rounded-full bg-zentra-accent" /> Limpieza programada
                </span>
            </div>
        </div>
    );
}
